// src/sim/economics.ts
// Cash economy: starting loadout, round-end earnings, AI auto-buy logic.
// Pure functions — no DOM, no side effects outside of the arrays passed in.

import { WEAPONS, WEAPONS_BY_ID, type WeaponDef } from './weapons';
import type { Tank, KillEntry } from './state';

export const STARTING_CASH = 10_000;

// Bonuses per "place point". Place points = (playerCount - deathOrderRank).
// A survivor's deathOrder is 0; they rank at the top and get playerCount points.
// First to die has deathOrder=1 and gets (playerCount - 1 + 1)... see computePlacement.
export const PLACE_BONUS_PER_POINT = 2_000;

// Cash awarded per kill credited (regardless of survival).
export const KILL_BONUS = 3_000;

/** Classic-SE-ish starter loadout. Baby missiles infinite; everything else scarce. */
export const STARTER_INVENTORY: Record<string, number> = {
  'baby-missile': Infinity,
  'missile': 3,
  'tracer': 5,
  'baby-digger': 2,
};

/**
 * Compute place points for each tank, where higher = better survival.
 *   Survivor(s):        playerCount points
 *   2nd-to-last:        2 points
 *   First to die:       1 point
 *
 * Uses tank.deathOrder (0 = still alive, 1 = first to die, N = Nth to die).
 */
export function computePlacePoints(tanks: Tank[]): number[] {
  const n = tanks.length;
  return tanks.map(t => {
    if (t.alive || t.deathOrder === 0) return n;          // survivor
    // deathOrder=1 (first to die) should map to 1 point.
    // deathOrder=2 to 2 points, etc.
    return Math.min(n - 1, t.deathOrder);
  });
}

/**
 * Per-tank kill count from the round's kill log.
 * Self-kills (killer === victim) still credit the killer (classic SE behavior).
 */
export function computeKillCounts(tanks: Tank[], killLog: KillEntry[]): number[] {
  const counts = tanks.map(() => 0);
  for (const entry of killLog) {
    if (entry.killer >= 0 && entry.killer < counts.length) counts[entry.killer]++;
  }
  return counts;
}

/** Earnings = place bonus + kill bonus. Returns $ amount per tank, same index as tanks[]. */
export function computeRoundEarnings(tanks: Tank[], killLog: KillEntry[]): number[] {
  const placePts = computePlacePoints(tanks);
  const kills = computeKillCounts(tanks, killLog);
  return tanks.map((_, i) => placePts[i] * PLACE_BONUS_PER_POINT + kills[i] * KILL_BONUS);
}

/** Mutates tank.cash in place. Returns the earnings array for display. */
export function applyRoundEarnings(tanks: Tank[], killLog: KillEntry[]): number[] {
  const earnings = computeRoundEarnings(tanks, killLog);
  for (let i = 0; i < tanks.length; i++) {
    tanks[i].cash += earnings[i];
  }
  return earnings;
}

// ---------------------- SHOP HELPERS ----------------------

/** Catalog ordering for the shop screen. Free items (baby-missile) are hidden. */
export function shopCatalog(): WeaponDef[] {
  return WEAPONS.filter(w => !w.isFree && w.cost > 0);
}

/** Can this tank afford one more of weaponId? Also enforces per-round stack cap. */
export function canAfford(tank: Tank, weaponId: string): boolean {
  const def = WEAPONS_BY_ID[weaponId];
  if (!def) return false;
  if (tank.cash < def.cost) return false;
  // Stack cap: default weapon qty is a reasonable ceiling per round; allow up to 2x that
  // so heavy buyers aren't hard-capped.
  const current = tank.inventory[weaponId] ?? 0;
  if (current !== Infinity && current >= def.qty * 2) return false;
  return true;
}

/** Buy one of weaponId. Returns true on success. Mutates tank in place. */
export function buyOne(tank: Tank, weaponId: string): boolean {
  if (!canAfford(tank, weaponId)) return false;
  const def = WEAPONS_BY_ID[weaponId];
  tank.cash -= def.cost;
  tank.inventory[weaponId] = (tank.inventory[weaponId] ?? 0) + 1;
  return true;
}

/** Refund one of weaponId. Returns true on success. */
export function sellOne(tank: Tank, weaponId: string): boolean {
  const def = WEAPONS_BY_ID[weaponId];
  if (!def) return false;
  const current = tank.inventory[weaponId] ?? 0;
  if (current === Infinity || current <= 0) return false;
  // Only refund items purchased this session — we don't track purchases separately,
  // so allow sell-back of anything the tank owns. Refund 100% (low-stakes game).
  tank.inventory[weaponId] = current - 1;
  if (tank.inventory[weaponId] === 0) delete tank.inventory[weaponId];
  tank.cash += def.cost;
  return true;
}

/**
 * AI auto-buy heuristic. Walks the catalog from most→least expensive and buys
 * what it can afford, up to a soft per-weapon cap of 2. Leaves ~20% cash as
 * reserve so the AI can stock up after a bad round.
 */
export function aiAutoBuy(tank: Tank): void {
  const reserve = Math.floor(tank.cash * 0.2);
  const affordable = () => tank.cash - reserve;
  const sorted = [...shopCatalog()].sort((a, b) => b.cost - a.cost);

  // Pass 1: one of each we can afford, priced-high to priced-low
  for (const def of sorted) {
    if (def.cost > affordable()) continue;
    // Skip weapons the AI already has 3+ of (already stocked)
    if ((tank.inventory[def.id] ?? 0) >= 3) continue;
    buyOne(tank, def.id);
  }
  // Pass 2: fill up on cheap staples (missile, baby-digger, tracer)
  const staples = ['missile', 'baby-digger', 'tracer', 'riot-charge'];
  for (const id of staples) {
    while (canAfford(tank, id) && WEAPONS_BY_ID[id].cost <= affordable()) {
      if (!buyOne(tank, id)) break;
    }
  }
}
